import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import {
  requestCustomVerification,
  checkCustomVerificationStatus,
} from "../lib/customContractsApi";
import { useAuth } from "../auth/useAuth";

import { ethers } from "ethers";

/**
 * ---------------------------------------------------------------------
 * KRIONEX — Deployments (SaaS)
 * ---------------------------------------------------------------------
 *
 * Immutable audit trail of deployments recorded by the backend.
 *
 * SaaS Rules:
 * - Deployments page is PRIVATE.
 * - Only the top header (AuthBar) handles Connect / Sign in / Sign out.
 * - This page never renders auth buttons.
 * - If not signed in, show a single clean gate message.
 *
 * Data:
 * - GET /deployments?mine=true&kind=deployment   (requires JWT)
 *
 * Verification UX goals:
 * - Reflect DB-backed verification lifecycle states clearly
 * - Prevent duplicate verify requests while verification is already in progress
 * - Prefer DB identifiers (requestId / txHash) over filesystem-only fallbacks
 * - Surface verification messages in a user-friendly, privacy-safe way
 */

function shorten(value) {
  if (!value || typeof value !== "string") return "-";
  if (value.length <= 12) return value;
  return `${value.slice(0, 6)}…${value.slice(-4)}`;
}

function safeString(value) {
  if (value === null || value === undefined) return "";
  if (typeof value === "string" && value.trim().length === 0) return "";
  return String(value);
}

function safeNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function formatSupplyDisplay(value, decimals = null, rawValue = null) {
  const hasValue = value != null && String(value).trim() !== "";
  if (hasValue) {
    const raw = String(value).trim();
    const num = Number(raw);
    if (Number.isFinite(num)) return num.toLocaleString();
    return raw;
  }

  const hasRaw = rawValue != null && String(rawValue).trim() !== "";
  if (hasRaw && decimals != null && String(decimals).trim() !== "") {
    try {
      const formatted = ethers.formatUnits(String(rawValue), Number(decimals));
      const num = Number(formatted);
      return Number.isFinite(num) ? num.toLocaleString() : formatted;
    } catch {
      return String(rawValue);
    }
  }

  return "-";
}

function formatDate(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString();
}

function normalizeStatus(status) {
  return String(status || "").trim().toLowerCase();
}

function humanStatus(status) {
  const s = normalizeStatus(status);

  if (s === "not_requested") return "Not requested";
  if (s === "queued") return "Queued";
  if (s === "submitting") return "Submitting";
  if (s === "pending") return "Pending";
  if (s === "verified") return "Verified";
  if (s === "retryable") return "Retry available";
  if (s === "failed") return "Failed";

  return s || "Unknown";
}

function badgeClass(status) {
  const s = normalizeStatus(status);

  if (s === "verified") {
    return "border-emerald-200 bg-emerald-50 text-emerald-700";
  }
  if (s === "pending") {
    return "border-[#E6D3A3] bg-[#FFF8E7] text-[#8A641E]";
  }
  if (s === "submitting") {
    return "border-slate-200 bg-slate-50 text-slate-700";
  }
  if (s === "queued") {
    return "border-[#E6D3A3] bg-[#FFF8E7] text-[#8A641E]";
  }
  if (s === "retryable") {
    return "border-[#E6D3A3] bg-[#FFF8E7] text-[#8A641E]";
  }
  if (s === "failed") {
    return "border-rose-200 bg-rose-50 text-rose-700";
  }

  return "border-slate-200 bg-slate-50 text-slate-700";
}

function isProcessingStatus(status) {
  const s = normalizeStatus(status);
  return s === "queued" || s === "submitting" || s === "pending";
}

function canTriggerVerification(status) {
  const s = normalizeStatus(status);
  return s === "not_requested" || s === "retryable" || s === "failed";
}

function actionLabelForStatus(status) {
  const s = normalizeStatus(status);
  if (s === "failed") return "Sync";
  if (s === "retryable") return "Retry";
  return "Verify";
}

function buildVerificationHint(row) {
  const status = normalizeStatus(row?.verificationStatus);
  const message = safeString(row?.verificationMessage);
  const attempts = safeNumber(row?.verificationAttempts, 0);
  const failureCategory = safeString(row?.verificationFailureCategory);

  if (message) return message;

  if (status === "queued") {
    return "Verification is queued and will start shortly.";
  }

  if (status === "submitting") {
    return "Submitting contract source to the explorer.";
  }

  if (status === "pending") {
    return "Waiting for explorer confirmation.";
  }

  if (status === "verified") {
    return "Contract verified successfully.";
  }

  if (status === "retryable") {
    return failureCategory
      ? `Temporary verification issue (${failureCategory}). You can retry.`
      : "Temporary verification issue. You can retry.";
  }

  if (status === "failed") {
    return attempts > 0
      ? `Verification failed after ${attempts} attempt${attempts > 1 ? "s" : ""}.`
      : "Verification failed.";
  }

  return "Verification has not been requested yet.";
}

function safeApiError(err, fallback = "Something went wrong.") {
  return (
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.message ||
    fallback
  );
}

function isCustomDeployment(row) {
  return String(row?.sourceMode || "").trim().toLowerCase() === "custom";
}

function getCustomVerificationTarget(row) {
  return {
    deploymentFile: row?.deploymentFile || row?.file || "",
    requestId: row?.requestId || "",
    txHash: row?.txHash || "",
  };
}

function isVerifiedCustomResponse(result) {
  const status = String(
    result?.verification?.status ||
    result?.status ||
    result?.deployment?.verificationStatus ||
    "",
  )
    .trim()
    .toLowerCase();

  const text = [
    result?.verification?.message,
    result?.message,
    result?.explorer?.result,
    result?.explorer?.message,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return (
    status === "verified" ||
    text.includes("already verified") ||
    text.includes("pass - verified") ||
    text.includes("successfully verified")
  );
}

function getCustomVerificationMessage(result, fallback = "Custom verification status updated.") {
  return (
    result?.verification?.message ||
    result?.message ||
    result?.error ||
    fallback
  );
}

/* ---------------------------------------------------------------------
 * UI primitives
 * ------------------------------------------------------------------- */
function cn(...values) {
  return values.filter(Boolean).join(" ");
}

function Surface({ className = "", children }) {
  return (
    <div
      className={cn(
        "rounded-[28px] border border-slate-200/80 bg-white shadow-[0_18px_55px_rgba(15,23,42,0.07)]",
        className,
      )}
    >
      {children}
    </div>
  );
}

function InlineNotice({ title, subtitle, tone = "neutral" }) {
  const toneClass =
    tone === "warning"
      ? "border-[#E6C978]/60 bg-[#FFF8E6] text-[#5B4215]"
      : tone === "error"
        ? "border-rose-200 bg-rose-50 text-rose-800"
        : tone === "success"
          ? "border-emerald-200 bg-emerald-50 text-emerald-800"
          : "border-slate-200 bg-white text-slate-800";

  return (
    <div className={cn("rounded-3xl border p-5 shadow-sm", toneClass)}>
      <div className="flex gap-4">
        <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl border border-current/10 bg-white/60 text-sm font-semibold">
          i
        </div>

        <div>
          <div className="text-sm font-semibold tracking-tight">{title}</div>
          {subtitle ? (
            <div className="mt-1 max-w-3xl text-sm leading-6 opacity-80">
              {subtitle}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, hint, tone = "neutral" }) {
  const toneClass =
    tone === "brand"
      ? "border-[#E6D3A3] bg-[#FFF8E7]"
      : tone === "success"
        ? "border-emerald-200 bg-emerald-50"
        : tone === "warning"
          ? "border-[#E6D3A3] bg-[#FFF8E7]"
          : "border-slate-200 bg-[#F8FAFC]";

  return (
    <div className={cn("rounded-2xl border p-4 shadow-sm", toneClass)}>
      <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
        {label}
      </div>
      <div className="mt-3 text-2xl font-semibold tracking-[-0.035em] text-[#050713]">
        {value}
      </div>
      {hint ? (
        <p className="mt-2 text-xs leading-5 text-slate-500">{hint}</p>
      ) : null}
    </div>
  );
}

function ActionButton({ className = "", children, ...props }) {
  return (
    <button
      {...props}
      className={cn(
        "inline-flex items-center justify-center rounded-2xl border border-white/10 bg-white/[0.08] px-4 py-2.5 text-sm font-semibold text-white shadow-sm backdrop-blur transition",
        "hover:-translate-y-0.5 hover:bg-white/[0.14] disabled:cursor-not-allowed disabled:translate-y-0 disabled:opacity-50",
        className,
      )}
    >
      {children}
    </button>
  );
}

export default function Deployments() {
  const { isAuthed, restoreLoading } = useAuth();

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);

  const [error, setError] = useState("");
  const [actionError, setActionError] = useState("");
  const [actionMessage, setActionMessage] = useState("");
  const [privacyMode, setPrivacyMode] = useState(true);

  // Track row-level verify request state
  const [verifyingKeys, setVerifyingKeys] = useState({});

  // Avoid race conditions between refreshes
  const reqIdRef = useRef(0);

  /* -------------------------------------------------------
     Load deployment history (SaaS: mine=true)
  ------------------------------------------------------- */
  const loadHistory = useCallback(async () => {
    if (restoreLoading || !isAuthed) {
      return;
    }
    const reqId = ++reqIdRef.current;

    setLoading(true);
    setError("");

    const controller = new AbortController();

    try {
      const res = await api.get("/deployments", {
        params: { mine: true, kind: "deployment" },
        headers: { "Cache-Control": "no-store" },
        signal: controller.signal,
      });

      if (reqId !== reqIdRef.current) return;

      const list = Array.isArray(res?.data?.records) ? res.data.records : [];
      setRecords(list);
    } catch (err) {
      if (reqId !== reqIdRef.current) return;
      if (err?.name === "CanceledError") return;

      console.error("[deployments] load failed:", err);
      setError(safeApiError(err, "Unable to load deployment history."));
      setRecords([]);
    } finally {
      if (reqId === reqIdRef.current) setLoading(false);
    }

    return () => controller.abort();
  }, [isAuthed, restoreLoading]);

  useEffect(() => {
    if (restoreLoading) return;
    if (!isAuthed) return;
    loadHistory();
  }, [restoreLoading, isAuthed, loadHistory]);

  /* -------------------------------------------------------
     Trigger verification request
  ------------------------------------------------------- */
  const handleVerify = useCallback(
    async (row) => {
      const actionKey =
        row.requestId ||
        row.txHash ||
        row.file ||
        row.customVersionId ||
        row.address ||
        "unknown";

      setActionError("");
      setActionMessage("");
      setVerifyingKeys((prev) => ({ ...prev, [actionKey]: true }));

      try {
        const status = normalizeStatus(row.verificationStatus);

        // Custom Solidity deployments must NOT use the standard /verify-contract flow.
        if (isCustomDeployment(row)) {
          const target = getCustomVerificationTarget(row);

          if (!target.deploymentFile && !target.requestId && !target.txHash) {
            throw new Error("Custom deployment reference missing for verification.");
          }

          const requestResult = await requestCustomVerification(target);

          if (!requestResult?.ok && !isVerifiedCustomResponse(requestResult)) {
            throw new Error(
              getCustomVerificationMessage(
                requestResult,
                "Custom verification request failed.",
              ),
            );
          }

          let finalResult = requestResult;

          // If explorer accepted/submitted, check status a few times.
          for (let i = 0; i < 6; i += 1) {
            const statusResult = await checkCustomVerificationStatus(target);

            if (statusResult) {
              finalResult = statusResult;
            }

            if (isVerifiedCustomResponse(statusResult)) {
              break;
            }

            await new Promise((resolve) => setTimeout(resolve, 3000));
          }

          setActionMessage(
            isVerifiedCustomResponse(finalResult)
              ? "Custom contract verification synced from explorer."
              : getCustomVerificationMessage(
                finalResult,
                "Custom verification requested. Explorer status may take a moment to update.",
              ),
          );

          await loadHistory();
          return;
        }

        // Standard failed deployment sync path stays unchanged.
        if (status === "failed") {
          const contractAddress =
            row.address ||
            row.contractAddress ||
            row.result?.contractAddress ||
            row.result?.address ||
            "";

          if (!row.network || !contractAddress) {
            throw new Error(
              "Unable to sync verification: missing network or contract address.",
            );
          }

          const res = await api.post("/verification/sync-status", {
            network: row.network,
            contractAddress,
            txHash: row.txHash || null,
            requestId: row.requestId || null,
          });

          if (!res?.data) {
            throw new Error("Verification sync failed.");
          }

          setActionMessage(
            res?.data?.message ||
            (res?.data?.verified
              ? "Verification status synced from explorer."
              : "Explorer does not show verified source code yet."),
          );

          await loadHistory();
          return;
        }

        // Standard deployment verification path stays unchanged.
        const payload = {};

        if (row.requestId) {
          payload.requestId = row.requestId;
        } else if (row.txHash) {
          payload.txHash = row.txHash;
        } else if (row.file) {
          payload.deploymentFile = row.file;
        } else {
          throw new Error(
            "Deployment is missing a stable identifier for verification.",
          );
        }

        const res = await api.post("/verify-contract", payload);

        if (!res?.data) {
          throw new Error("Verification request failed.");
        }

        setActionMessage(
          res?.data?.message || "Verification requested successfully.",
        );

        await loadHistory();
      } catch (err) {
        console.error("[verify] failed:", err);
        setActionError(safeApiError(err, "Verification request failed."));
      } finally {
        setVerifyingKeys((prev) => {
          const next = { ...prev };
          delete next[actionKey];
          return next;
        });
      }
    },
    [loadHistory],
  );

  /* -------------------------------------------------------
     Normalize backend records for UI
  ------------------------------------------------------- */
  const rows = useMemo(() => {
    return (records || []).map((r, idx) => {
      const kind = safeString(r?.kind || "deployment");

      const tokenType = safeString(
        r?.tokenType || r?.token?.tokenType || r?.type || "",
      );
      const sourceMode = safeString(r?.sourceMode || "standard");
      const customVersionId = safeString(r?.customVersionId || "");
      const contractName = safeString(r?.contractName || "");

      const tokenName = safeString(
        r?.token?.tokenName ||
        r?.tokenName ||
        r?.contractName ||
        r?.name ||
        "",
      );

      const decimals = r?.token?.decimals ?? r?.decimals ?? null;
      const initialSupplyHuman =
        r?.token?.initialSupplyHuman ?? r?.initialSupplyHuman ?? null;
      const initialSupply = r?.token?.initialSupply ?? r?.initialSupply ?? null;

      const network = safeString(r?.network);
      const address = safeString(r?.address || r?.contractAddress);
      const txHash = safeString(r?.txHash);
      const requestId = safeString(r?.requestId);

      const explorerUrl = safeString(r?.explorerUrl || r?.etherscanUrl);
      const explorerTxUrl = safeString(r?.explorerTxUrl);
      const download = safeString(r?.download);

      const verificationStatus = safeString(
        r?.verificationStatus || r?.verification?.status || "not_requested",
      );
      const verificationMessage = safeString(
        r?.verificationMessage || r?.verification?.message || "",
      );
      const verificationAttempts = safeNumber(
        r?.verificationAttempts ?? r?.verification?.attempts,
        0,
      );
      const verificationFailureReason = safeString(
        r?.verificationFailureReason || r?.verification?.failureReason || "",
      );
      const verificationFailureCategory = safeString(
        r?.verificationFailureCategory || r?.verification?.failureCategory || "",
      );
      const verificationJobId = safeString(
        r?.verificationJobId || r?.verification?.jobId || "",
      );
      const verificationRequestedAt =
        r?.verificationRequestedAt || r?.verification?.requestedAt || null;
      const verificationStartedAt =
        r?.verificationStartedAt || r?.verification?.startedAt || null;
      const verificationUpdatedAt =
        r?.verificationUpdatedAt || r?.verification?.updatedAt || null;
      const verificationCompletedAt =
        r?.verificationCompletedAt || r?.verification?.completedAt || null;
      const verifiedAt = r?.verifiedAt || r?.verification?.verifiedAt || null;

      const createdAt = r?.createdAt || r?.deployedAt || r?.confirmedAt || null;

      const row = {
        key: requestId || txHash || r?.file || address || `row-${idx}`,
        file: r?.file || null,
        requestId: requestId || null,
        kind,
        sourceMode,
        isCustom: sourceMode.toLowerCase() === "custom",
        customVersionId,
        contractName,
        tokenName,
        decimals,
        initialSupplyHuman,
        initialSupply,
        tokenType,
        network,
        address,
        txHash,
        explorerUrl: explorerUrl || null,
        explorerTxUrl: explorerTxUrl || null,
        download: download || null,
        verificationStatus,
        verificationStatusNorm: normalizeStatus(verificationStatus),
        verificationMessage,
        verificationAttempts,
        verificationFailureReason,
        verificationFailureCategory,
        verificationJobId,
        verificationRequestedAt,
        verificationStartedAt,
        verificationUpdatedAt,
        verificationCompletedAt,
        verifiedAt,
        createdAt,
      };

      return {
        ...row,
        verificationHint: buildVerificationHint(row),
      };
    });
  }, [records]);

  const deploymentCount = rows.length;

  const verifiedCount = useMemo(
    () => rows.filter((r) => normalizeStatus(r.verificationStatus) === "verified").length,
    [rows],
  );

  const inProgressCount = useMemo(
    () => rows.filter((r) => isProcessingStatus(r.verificationStatus)).length,
    [rows],
  );

  const retryableCount = useMemo(
    () =>
      rows.filter((r) => {
        const s = normalizeStatus(r.verificationStatus);
        return s === "retryable" || s === "failed";
      }).length,
    [rows],
  );

  /* -------------------------------------------------------
     Render (SaaS gate handled here)
  ------------------------------------------------------- */
  return (
    <div className="space-y-6">
      {!isAuthed && (
        <InlineNotice
          title="Connect wallet to load deployments"
          subtitle="Preview the registry below. After sign-in, Krionex loads your private deployment records, verification status, and explorer links."
          tone="warning"
        />
      )}
      <div className="pointer-events-none fixed inset-0 -z-10 bg-[radial-gradient(circle_at_top_left,_rgba(197,155,69,0.10),_transparent_30%),radial-gradient(circle_at_top_right,_rgba(15,23,42,0.06),_transparent_26%),linear-gradient(to_bottom,_#F7F8FA,_#F1F3F6_42%,_#F7F8FA)]" />

      <div className="relative overflow-hidden rounded-[32px] border border-[#2A2418] bg-[#050713] p-6 text-white shadow-[0_24px_70px_rgba(5,7,19,0.28)] sm:p-8">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(197,155,69,0.24),_transparent_30%),radial-gradient(circle_at_bottom_right,_rgba(255,255,255,0.08),_transparent_32%)]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050713] via-[#050713]/95 to-[#111827]/90" />
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#E6C978]/60 to-transparent" />
        <div className="relative">
          <div className="flex flex-col gap-6 xl:flex-row xl:items-start xl:justify-between">
            <div className="max-w-3xl">
              <div className="inline-flex items-center rounded-full border border-white/10 bg-white/[0.07] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#E6C978] backdrop-blur">
                Deployment registry
              </div>

              <h1 className="mt-4 max-w-3xl text-3xl font-semibold tracking-[-0.045em] text-white sm:text-4xl">
                Track every contract launch from one private registry.
              </h1>

              <p className="mt-3 max-w-2xl text-sm leading-7 text-slate-300 sm:text-base">
                Review deployment records, monitor explorer verification, protect sensitive addresses with privacy mode, and reopen contracts in Operations when follow-up actions are needed.
              </p>

              <div className="mt-5 flex flex-wrap gap-3">
                <ActionButton onClick={loadHistory} disabled={loading}>
                  {loading ? "Refreshing…" : "Refresh Deployments"}
                </ActionButton>

                <label className="inline-flex items-center gap-2 rounded-2xl border border-white/10 bg-white/[0.07] px-4 py-2.5 text-sm font-semibold text-white backdrop-blur transition hover:bg-white/[0.12]">
                  <input
                    type="checkbox"
                    checked={privacyMode}
                    onChange={(e) => setPrivacyMode(e.target.checked)}
                  />
                  Privacy Mode
                </label>
              </div>
            </div>

            <div className="grid w-full gap-3 sm:grid-cols-2 xl:max-w-lg">
              <div className="rounded-2xl border border-white/10 bg-white/[0.06] p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] backdrop-blur">
                <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-300">
                  Deployments
                </div>
                <div className="mt-3 text-3xl font-semibold tracking-tight text-white">
                  {deploymentCount}
                </div>
                <p className="mt-2 text-xs leading-5 text-slate-300">
                  Records currently loaded from your private deployment history.
                </p>
              </div>

              <div className="rounded-2xl border border-white/10 bg-white/[0.06] p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] backdrop-blur">
                <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-300">
                  Verified
                </div>
                <div className="mt-3 text-3xl font-semibold tracking-tight text-white">
                  {verifiedCount}
                </div>
                <p className="mt-2 text-xs leading-5 text-slate-300">
                  Contracts with successful explorer verification.
                </p>
              </div>

              <div className="rounded-2xl border border-white/10 bg-white/[0.06] p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] backdrop-blur">
                <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-300">
                  In Progress
                </div>
                <div className="mt-3 text-3xl font-semibold tracking-tight text-white">
                  {inProgressCount}
                </div>
                <p className="mt-2 text-xs leading-5 text-slate-300">
                  Verification jobs that are queued, submitting, or pending.
                </p>
              </div>

              <div className="rounded-2xl border border-white/10 bg-white/[0.06] p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] backdrop-blur">
                <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-300">
                  Retry Queue
                </div>
                <div className="mt-3 text-3xl font-semibold tracking-tight text-white">
                  {retryableCount}
                </div>
                <p className="mt-2 text-xs leading-5 text-slate-300">
                  Records that may need another verification attempt.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {actionMessage ? (
        <InlineNotice
          title="Verification request accepted"
          subtitle={actionMessage}
          tone="success"
        />
      ) : null}

      {actionError ? (
        <InlineNotice
          title="Unable to request verification"
          subtitle={actionError}
          tone="error"
        />
      ) : null}

      {error ? (
        <InlineNotice
          title="Unable to load deployment history"
          subtitle={error}
          tone="error"
        />
      ) : null}

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_240px] 2xl:grid-cols-[minmax(0,1fr)_260px]">
        <div className="space-y-6">
          <Surface className="overflow-hidden bg-white">
            <div className="border-b border-slate-200/70 px-6 py-5">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <div className="inline-flex rounded-full border border-[#E6D3A3] bg-[#FFF8E7] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#9A7226]">
                    Records
                  </div>
                  <h2 className="mt-3 text-xl font-semibold tracking-[-0.035em] text-[#050713]">
                    Deployment registry
                  </h2>
                  <p className="mt-1 text-sm leading-6 text-slate-500">
                    Immutable deployment records with verification lifecycle state,
                    explorer links, and direct workflow access into Operations.
                  </p>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-[#F8FAFC] px-4 py-3 text-sm text-slate-600 shadow-sm">
                  Showing <span className="font-semibold text-slate-900">{deploymentCount}</span>{" "}
                  deployment{deploymentCount === 1 ? "" : "s"}
                  {privacyMode ? (
                    <span className="ml-2 inline-flex rounded-full border border-[#E6D3A3] bg-[#FFF8E7] px-2 py-0.5 text-[11px] font-semibold text-[#8A641E]">
                      Privacy on
                    </span>
                  ) : null}
                </div>
              </div>
            </div>

            <div className="p-6">
              {loading ? (
                <div className="space-y-3">
                  <div className="h-16 animate-pulse rounded-2xl bg-slate-100" />
                  <div className="h-16 animate-pulse rounded-2xl bg-slate-100" />
                  <div className="h-16 animate-pulse rounded-2xl bg-slate-100" />
                </div>
              ) : !error && rows.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-slate-200 bg-white/70 p-8 text-center">
                  <div className="text-base font-semibold text-slate-900">
                    No deployment records yet
                  </div>
                  <p className="mt-2 text-sm leading-6 text-slate-500">
                    Once deployments are recorded by Krionex, they will appear here with
                    verification state and explorer access.
                  </p>
                  <div className="mt-5 flex justify-center">
                    <Link
                      to="/"
                      className="inline-flex items-center justify-center rounded-2xl bg-[#050713] px-4 py-2.5 text-sm font-semibold text-white shadow-[0_14px_30px_rgba(5,7,19,0.18)] transition hover:-translate-y-0.5 hover:bg-[#111827]"
                    >
                      Open Studio
                    </Link>
                  </div>
                </div>
              ) : !error ? (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[1080px] border-separate border-spacing-0">
                    <thead>
                      <tr>
                        <th className="border-b border-slate-200 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                          Deployment
                        </th>
                        <th className="border-b border-slate-200 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                          Supply
                        </th>
                        <th className="border-b border-slate-200 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                          Network
                        </th>
                        <th className="border-b border-slate-200 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                          Contract
                        </th>
                        <th className="border-b border-slate-200 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                          Verification
                        </th>
                        <th className="border-b border-slate-200 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                          Created
                        </th>
                        <th className="border-b border-slate-200 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                          Bundle
                        </th>
                        <th className="border-b border-slate-200 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                          Actions
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {rows.map((d) => {
                        const isProcessing = isProcessingStatus(
                          d.verificationStatusNorm,
                        );
                        const canVerify = canTriggerVerification(
                          d.verificationStatusNorm,
                        );
                        const isVerifying = Boolean(verifyingKeys[d.key]);
                        const actionLabel = d.isCustom
                          ? d.verificationStatusNorm === "verified"
                            ? "Complete"
                            : "Sync"
                          : actionLabelForStatus(d.verificationStatusNorm);

                        return (
                          <tr key={d.key} className="align-top transition hover:bg-[#FFFDF7]">
                            <td className="border-b border-slate-100 px-4 py-4">
                              <div className="space-y-1">
                                <div className="text-sm font-semibold text-slate-900">
                                  {d.tokenName || "Untitled deployment"}
                                </div>
                                <div className="flex flex-wrap items-center gap-2">
                                  {d.isCustom ? (
                                    <span className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                      Custom
                                    </span>
                                  ) : null}
                                  <span className="inline-flex rounded-full border border-[#E6D3A3] bg-[#FFF8E7] px-2.5 py-1 text-[11px] font-semibold text-[#8A641E]">
                                    {d.tokenType || "-"}
                                  </span>
                                </div>
                              </div>
                            </td>

                            <td className="border-b border-slate-100 px-4 py-4">
                              {d.tokenType === "ERC20" ? (
                                <div className="space-y-1">
                                  <div className="text-sm font-semibold text-slate-900">
                                    {formatSupplyDisplay(d.initialSupplyHuman, d.decimals, d.initialSupply)}
                                  </div>
                                  <div className="text-xs text-slate-500">
                                    Decimals: {d.decimals ?? 18}
                                  </div>
                                </div>
                              ) : (
                                <span className="text-xs text-slate-400">-</span>
                              )}
                            </td>


                            <td className="border-b border-slate-100 px-4 py-4">
                              <div className="text-sm font-medium text-slate-900">
                                {d.network || "-"}
                              </div>
                            </td>

                            <td className="border-b border-slate-100 px-4 py-4">
                              <div className="space-y-2">
                                <div className="text-sm font-medium text-slate-900">
                                  {privacyMode ? (
                                    <span className="text-slate-400">Hidden</span>
                                  ) : d.explorerUrl ? (
                                    <a
                                      href={d.explorerUrl}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="text-[#8A641E] hover:text-[#050713] hover:underline"
                                      title={d.address}
                                    >
                                      {shorten(d.address)}
                                    </a>
                                  ) : d.address ? (
                                    <span title={d.address}>{shorten(d.address)}</span>
                                  ) : (
                                    "-"
                                  )}
                                </div>

                                <div className="text-xs text-slate-500">
                                  Tx:{" "}
                                  {privacyMode ? (
                                    <span className="text-slate-400">Hidden</span>
                                  ) : d.explorerTxUrl ? (
                                    <a
                                      href={d.explorerTxUrl}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="text-[#8A641E] hover:text-[#050713] hover:underline"
                                      title={d.txHash}
                                    >
                                      {shorten(d.txHash)}
                                    </a>
                                  ) : d.txHash ? (
                                    <span title={d.txHash}>{shorten(d.txHash)}</span>
                                  ) : (
                                    "-"
                                  )}
                                </div>
                              </div>
                            </td>

                            <td className="border-b border-slate-100 px-4 py-4">
                              <div className="max-w-[280px] space-y-2">
                                <div className="flex flex-wrap items-center gap-2">
                                  <span
                                    className={cn(
                                      "inline-flex rounded-full border px-2.5 py-1 text-[11px] font-semibold",
                                      badgeClass(d.verificationStatus),
                                    )}
                                    title={d.verificationHint}
                                  >
                                    {humanStatus(d.verificationStatus)}
                                  </span>

                                  {d.verificationAttempts > 0 ? (
                                    <span
                                      className="text-[11px] text-slate-500"
                                      title="Verification attempts"
                                    >
                                      {d.verificationAttempts} attempt
                                      {d.verificationAttempts > 1 ? "s" : ""}
                                    </span>
                                  ) : null}
                                </div>

                                <p
                                  className="text-xs leading-5 text-slate-500"
                                  title={
                                    d.verificationFailureReason ||
                                    d.verificationMessage ||
                                    d.verificationHint
                                  }
                                >
                                  {d.verificationFailureReason ||
                                    d.verificationMessage ||
                                    d.verificationHint}
                                </p>

                                {d.verificationJobId ? (
                                  <p className="text-[11px] text-slate-400">
                                    Job: {shorten(d.verificationJobId)}
                                  </p>
                                ) : null}
                              </div>
                            </td>

                            <td className="border-b border-slate-100 px-4 py-4">
                              <div className="space-y-1">
                                <div className="text-sm text-slate-900">
                                  {formatDate(d.createdAt)}
                                </div>
                                {!privacyMode && d.verifiedAt ? (
                                  <div className="text-[11px] text-slate-400">
                                    Verified: {formatDate(d.verifiedAt)}
                                  </div>
                                ) : null}
                              </div>
                            </td>
                            <td className="border-b border-slate-100 px-4 py-4">
                              {d.download ? (
                                <a
                                  href={d.download}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="inline-flex items-center justify-center rounded-xl border border-[#E6D3A3] bg-[#FFF8E7] px-3 py-2 text-xs font-semibold text-[#8A641E] shadow-sm transition hover:bg-white"
                                  title="Download deployment bundle"
                                >
                                  Download
                                </a>
                              ) : d.isCustom ? (
                                <span className="text-xs text-slate-500">Custom source managed</span>
                              ) : (
                                <span className="text-xs text-slate-400">No bundle</span>
                              )}
                            </td>

                            <td className="border-b border-slate-100 px-4 py-4">
                              <div className="flex min-w-[150px] flex-col items-start gap-2">
                                {canVerify ? (
                                  <button
                                    onClick={() => handleVerify(d)}
                                    className="inline-flex items-center justify-center rounded-xl border border-[#E6D3A3] bg-[#FFF8E7] px-3 py-2 text-xs font-semibold text-[#8A641E] shadow-sm transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-50"
                                    disabled={isVerifying || isProcessing}
                                    title={
                                      isProcessing
                                        ? "Verification is already in progress."
                                        : undefined
                                    }
                                  >
                                    {isVerifying ? "Requesting…" : actionLabel}
                                  </button>
                                ) : isProcessing ? (
                                  <span className="text-xs font-medium text-slate-500">
                                    In progress
                                  </span>
                                ) : d.verificationStatusNorm === "verified" ? (
                                  <span className="text-xs font-semibold text-emerald-700">
                                    Complete
                                  </span>
                                ) : (
                                  <span className="text-xs text-slate-400">—</span>
                                )}

                                {privacyMode || !d.address ? (
                                  <span className="text-xs text-slate-400">Ops link hidden</span>
                                ) : (
                                  <a
                                    href={`/dashboard?network=${encodeURIComponent(
                                      d.network || "",
                                    )}&address=${encodeURIComponent(d.address)}`}
                                    className="text-xs font-semibold text-[#8A641E] hover:text-[#050713] hover:underline"
                                    title="Open this token in Operations"
                                  >
                                    Open in Ops
                                  </a>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  {!privacyMode ? (
                    <div className="mt-4 rounded-2xl border border-[#E6D3A3] bg-[#FFF8E7] px-4 py-3">
                      <p className="text-xs font-medium text-[#4B3410]">
                        Explorer verification can complete before Krionex updates this record.
                        If a contract appears unverified here, click the contract address and confirm
                        the latest verification status directly on the explorer.
                      </p>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          </Surface>
        </div>

        <div className="space-y-6">
          <StatCard
            label="Verified Rate"
            value={deploymentCount > 0 ? `${Math.round((verifiedCount / deploymentCount) * 100)}%` : "0%"}
            hint="Share of loaded deployments that have completed verification."
            tone="success"
          />

          <StatCard
            label="Privacy Mode"
            value={privacyMode ? "Enabled" : "Disabled"}
            hint="When enabled, addresses and transaction hashes stay hidden in the UI."
            tone={privacyMode ? "warning" : "brand"}
          />

          <Surface className="overflow-hidden bg-white">
            <div className="p-6">
              <div className="inline-flex rounded-full border border-[#E6D3A3] bg-[#FFF8E7] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#9A7226]">
                Verification Guide
              </div>

              <h3 className="mt-3 text-lg font-semibold tracking-tight text-slate-900">
                How verification flows work
              </h3>

              <div className="mt-4 space-y-3">
                <div className="rounded-2xl border border-slate-200 bg-[#F8FAFC] p-4 shadow-sm">
                  <div className="text-sm font-semibold text-slate-900">Queued / Submitting / Pending</div>
                  <p className="mt-1 text-xs leading-5 text-slate-500">
                    Verification is already moving through the explorer pipeline.
                  </p>
                </div>

                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 shadow-sm">
                  <div className="text-sm font-semibold text-slate-900">Verified</div>
                  <p className="mt-1 text-xs leading-5 text-slate-500">
                    Explorer confirmation completed successfully for this contract.
                  </p>
                </div>

                <div className="rounded-2xl border border-[#E6D3A3] bg-[#FFF8E7] p-4 shadow-sm">
                  <div className="text-sm font-semibold text-slate-900">Retry available / Failed</div>
                  <p className="mt-1 text-xs leading-5 text-slate-500">
                    You can trigger another verification attempt when eligible.
                  </p>
                </div>
              </div>
            </div>
          </Surface>

          <Surface className="overflow-hidden border border-[#1F2937]">
            <div className="bg-[#050713] p-6 text-white">
              <div className="text-xs font-semibold uppercase tracking-[0.16em] text-[#E6C978]">
                Workflow shortcut
              </div>
              <div className="mt-3 text-xl font-semibold tracking-tight">
                Continue contract operations
              </div>
              <p className="mt-2 text-sm leading-6 text-white/85">
                Open a deployment in Operations to inspect token context, infer templates,
                and run follow-up wallet actions from the same workspace.
              </p>

              <div className="mt-5 flex flex-wrap gap-3">
                <Link
                  to="/dashboard"
                  className="inline-flex items-center justify-center rounded-2xl bg-white px-4 py-3 text-sm font-semibold text-[#050713] transition hover:bg-[#FFF8E7]"
                >
                  Open Operations
                </Link>
                <Link
                  to="/help-center"
                  className="inline-flex items-center justify-center rounded-2xl border border-white/15 px-4 py-3 text-sm font-semibold text-white transition hover:bg-white/10"
                >
                  Contact Support
                </Link>
              </div>
            </div>
          </Surface>
        </div>
      </div>
    </div>

  );
}